import React, { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { openCreateNewCourseDialog, getAllCourses } from "../Redux/Dashboard/DashboardSlice";
import Dialog from "@mui/material/Dialog";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { IoCloseCircleOutline, IoTrashOutline, IoAdd } from "react-icons/io5";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFilm, faImage, faPen, faFolderOpen, faEdit } from '@fortawesome/free-solid-svg-icons';
import { Notify } from "../CommonPopup/Popup";
import Spinner from '../Spinner/Spinner';
import '../Style/CreateCourse.css';
import { useNavigate } from "react-router-dom";
import { faYoutube } from "@fortawesome/free-brands-svg-icons";
import { createCourses } from "../Redux/Auth/AuthSlice";

const CreateCourse = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showCreateNewCourseDialog, loader } = useSelector((s) => s.DashboardSlice);

  const [courseStructure, setCourseStructure] = useState([]);
  const [thumbnail, setThumbnail] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedLessonIdx, setSelectedLessonIdx] = useState(null);
  const [selectedSubjectIdx, setSelectedSubjectIdx] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showVideoPrompt, setShowVideoPrompt] = useState(false);
  const [isYouTube, setIsYouTube] = useState(false);
  const [youTubeUrl, setYouTubeUrl] = useState("");
  const [youTubeError, setYouTubeError] = useState("");
  const [activeButtonId, setActiveButtonId] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testGroups, setTestGroups] = useState([]);
  const [selectedTestGroupIdx, setSelectedTestGroupIdx] = useState(null);
  const [editingTestGroupIdx, setEditingTestGroupIdx] = useState(null);
  const [editingQuestionIdx, setEditingQuestionIdx] = useState(null);

  // Ref to prevent double submissions
  const isSubmittingRef = useRef(false);

  const { register, handleSubmit, reset, formState: { errors }, getValues } = useForm({
    defaultValues: { type: "Courses" }
  });

  // Form for adding/editing test groups
  const {
    register: registerTestGroup,
    handleSubmit: handleTestGroupSubmit,
    reset: resetTestGroup,
    formState: { errors: testGroupErrors },
    setValue: setTestGroupValue
  } = useForm();

  // Form for adding/editing questions
  const {
    register: registerQuestion,
    handleSubmit: handleQuestionSubmit,
    reset: resetQuestion,
    formState: { errors: questionErrors },
    watch: watchQuestion,
    setValue: setQuestionValue
  } = useForm({
    defaultValues: {
      question: "",
      options: ["", "", "", "", ""],
      answer: ""
    }
  });

  const getYouTubeVideoId = (url) => {
    try {
      const regex = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/;
      const match = url.match(regex);
      return match && match[1] ? match[1] : null;
    } catch (error) {
      console.error('Error parsing YouTube URL:', error);
      return null;
    }
  };

  const fetchYouTubeVideoTitle = async (url) => {
    try {
      const response = await fetch(`https://noembed.com/embed?format=json&url=${encodeURIComponent(url)}`);
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      return data.title || null;
    } catch (error) {
      console.error('Error fetching YouTube video title:', error);
      return null;
    }
  };

  const addVideoContent = async () => {
    if (isYouTube) {
      if (!youTubeUrl) {
        setYouTubeError("Please enter a YouTube URL.");
        return;
      }
      const videoId = getYouTubeVideoId(youTubeUrl);
      if (!videoId) {
        setYouTubeError("Invalid YouTube URL.");
        return;
      }
      const videoTitle = await fetchYouTubeVideoTitle(youTubeUrl);
      if (!videoTitle) {
        setYouTubeError("Failed to fetch video title. Please try again.");
        return;
      }
      const updated = [...courseStructure];
      updated[selectedLessonIdx].subjects[selectedSubjectIdx].content.video.push({
        name: videoTitle,
        url: youTubeUrl,
        type: "youtube"
      });
      setCourseStructure(updated);
      setYouTubeUrl("");
      setYouTubeError("");
      setShowVideoPrompt(false);
      Notify({ data: "success", msg: "YouTube video added successfully", type: "success" });
    } else {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "video/mp4";
      input.multiple = true;
      input.onchange = (e) => {
        const files = Array.from(e.target.files).map((file) => ({
          name: file.name,
          url: URL.createObjectURL(file),
          fileData: file,
          type: "file"
        }));
        const updated = [...courseStructure];
        updated[selectedLessonIdx].subjects[selectedSubjectIdx].content.video = [
          ...updated[selectedLessonIdx].subjects[selectedSubjectIdx].content.video,
          ...files
        ];
        setCourseStructure(updated);
        setShowVideoPrompt(false);
        Notify({ data: "success", msg: "Video file(s) added successfully", type: "success" });
      };
      input.click();
    }
  };

  const handleClose = () => {
    reset();
    setCourseStructure([]);
    setThumbnail(null);
    setIsPaid(false);
    setCurrentStep(1);
    setSelectedLessonIdx(null);
    setSelectedSubjectIdx(null);
    setShowSidebar(false);
    setShowVideoPrompt(false);
    setIsYouTube(false);
    setYouTubeUrl("");
    setYouTubeError("");
    setActiveButtonId(null);
    setShowTestModal(false);
    setTestGroups([]);
    setSelectedTestGroupIdx(null);
    setEditingTestGroupIdx(null);
    setEditingQuestionIdx(null);
    dispatch(openCreateNewCourseDialog(false));
  };

  const addLesson = () => {
    const values = getValues();
    const hasErrors = Object.keys(errors).length > 0;
    const isTitleValid = values.title && values.title.trim() !== "";
    const isDescriptionValid = values.description && values.description.trim() !== "";
    const isThumbnailValid = thumbnail !== null;

    if (!isTitleValid || !isDescriptionValid || !isThumbnailValid || hasErrors) {
      Notify({
        data: "error",
        msg: "Please fill all course details.",
        type: "error"
      });
      return;
    }

    const newLesson = { lesson_name: "", description: "", thumbnail: null, subjects: [] };
    setCourseStructure([...courseStructure, newLesson]);
    setSelectedLessonIdx(courseStructure.length);
    setSelectedSubjectIdx(null);
    setCurrentStep(2);
    setShowSidebar(true);
    Notify({ data: "success", msg: "Lesson added successfully", type: "success" });
  };

  const deleteLesson = (lessonIdx) => {
    const updated = courseStructure.filter((_, idx) => idx !== lessonIdx);
    setCourseStructure(updated);
    if (selectedLessonIdx === lessonIdx) {
      if (updated.length > 0) {
        const newIdx = lessonIdx < updated.length ? lessonIdx : updated.length - 1;
        setSelectedLessonIdx(newIdx);
        setSelectedSubjectIdx(null);
        setCurrentStep(2);
      } else {
        setSelectedLessonIdx(null);
        setSelectedSubjectIdx(null);
        setCurrentStep(1);
      }
    }
  };

  const addSubject = (lessonIdx, navigate = false) => {
    if (lessonIdx === null) return;
    const updated = [...courseStructure];
    updated[lessonIdx].subjects.push({
      chapter_name: "",
      description: "",
      thumbnail: null,
      content: { pdf: [], video: [], image: [], test: [] }
    });
    setCourseStructure(updated);
    if (navigate) {
      setSelectedLessonIdx(lessonIdx);
      setSelectedSubjectIdx(updated[lessonIdx].subjects.length - 1);
      setCurrentStep(3);
    }
  };

  const deleteSubject = (lessonIdx, subjectIdx) => {
    const updated = [...courseStructure];
    updated[lessonIdx].subjects = updated[lessonIdx].subjects.filter((_, idx) => idx !== subjectIdx);
    setCourseStructure(updated);
    if (selectedLessonIdx === lessonIdx && selectedSubjectIdx === subjectIdx) {
      setSelectedSubjectIdx(null);
      setCurrentStep(2);
    }
  };

  const deleteContent = (type, fileIdx) => {
    if (selectedLessonIdx === null || selectedSubjectIdx === null) return;
    const updated = [...courseStructure];
    updated[selectedLessonIdx].subjects[selectedSubjectIdx].content[type] =
      updated[selectedLessonIdx].subjects[selectedSubjectIdx].content[type].filter((_, idx) => idx !== fileIdx);
    setCourseStructure(updated);
    Notify({ data: "success", msg: `${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`, type: "success" });
  };

  const handleChange = (level, lIdx, sIdx, field, value) => {
    const updated = [...courseStructure];
    let target = updated[lIdx];
    if (sIdx !== null) target = target.subjects[sIdx];
    if (field === "thumbnail") {
      target[field] = value.target.files[0] || null;
    } else {
      target[field] = value;
    }
    setCourseStructure(updated);
  };

  const addContent = (type, isEdit = false, testIndex = null) => {
    if (selectedLessonIdx === null || selectedSubjectIdx === null) return;
    if (type === "video") {
      setShowVideoPrompt(true);
      setIsYouTube(false);
      setYouTubeUrl("");
      setYouTubeError("");
      return;
    }
    if (type === "test") {
      setShowTestModal(true);
      // Load all existing test groups for the subject
      const existingTestGroups = [...courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].content.test];
      setTestGroups(existingTestGroups);
      if (isEdit && testIndex !== null) {
        // Select the test group being edited
        setSelectedTestGroupIdx(testIndex);
      } else {
        // No specific group selected; allow adding new groups
        setSelectedTestGroupIdx(existingTestGroups.length > 0 ? 0 : null);
      }
      setEditingTestGroupIdx(null);
      setEditingQuestionIdx(null);
      return;
    }
    const accept = type === "pdf" ? ".pdf" : type === "image" ? "image/*" : "*";
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files).map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
        fileData: file,
        type: "file"
      }));
      const updated = [...courseStructure];
      updated[selectedLessonIdx].subjects[selectedSubjectIdx].content[type] = [
        ...updated[selectedLessonIdx].subjects[selectedSubjectIdx].content[type],
        ...files
      ];
      setCourseStructure(updated);
      Notify({ data: "success", msg: `${type.charAt(0).toUpperCase() + type.slice(1)} file(s) added successfully`, type: "success" });
    };
    input.click();
  };

  const editTestGroupInContent = (testIndex) => {
    addContent("test", true, testIndex);
  };

  const addTestGroup = (data) => {
    if (editingTestGroupIdx !== null) {
      setTestGroups((prevGroups) => {
        const updatedGroups = [...prevGroups];
        updatedGroups[editingTestGroupIdx] = {
          ...updatedGroups[editingTestGroupIdx],
          name: data.testGroupName
        };
        return updatedGroups;
      });
      setEditingTestGroupIdx(null);
      resetTestGroup();
      Notify({ data: "success", msg: "Test group updated successfully", type: "success" });
    } else {
      const newGroup = {
        name: data.testGroupName,
        tests: []
      };
      setTestGroups([...testGroups, newGroup]);
      setSelectedTestGroupIdx(testGroups.length);
      resetTestGroup();
      Notify({ data: "success", msg: "Test group added successfully", type: "success" });
    }
  };

  const editTestGroup = (groupIdx) => {
    setEditingTestGroupIdx(groupIdx);
    setTestGroupValue("testGroupName", testGroups[groupIdx].name);
  };

  const addQuestion = (data) => {
    console.log('addQuestion called with data:', data);

    if (isSubmittingRef.current) {
      console.log('Submission blocked: already in progress');
      return;
    }
    isSubmittingRef.current = true;

    const submissionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    console.log('Submission ID:', submissionId);

    let hasUpdatedState = false;

    if (!data.question || !data.options.every((opt) => opt) || !data.answer) {
      Notify({
        data: "error",
        msg: "Please fill all question details correctly",
        type: "error"
      });
      isSubmittingRef.current = false;
      return;
    }

    if (selectedTestGroupIdx === null || selectedTestGroupIdx >= testGroups.length) {
      Notify({
        data: "error",
        msg: "No test group selected",
        type: "error"
      });
      isSubmittingRef.current = false;
      return;
    }

    const newQuestion = {
      question: data.question,
      options: [...data.options],
      answer: data.answer
    };

    setTestGroups((prevGroups) => {
      if (hasUpdatedState) {
        console.log('setTestGroups skipped: state already updated for submission', submissionId);
        return prevGroups;
      }

      console.log('setTestGroups called for submission', submissionId);
      hasUpdatedState = true;

      const updatedGroups = [...prevGroups];
      if (editingQuestionIdx !== null) {
        updatedGroups[selectedTestGroupIdx].tests[editingQuestionIdx] = newQuestion;
        Notify({ data: "success", msg: "Question updated successfully", type: "success" });
      } else {
        updatedGroups[selectedTestGroupIdx].tests = [...updatedGroups[selectedTestGroupIdx].tests, newQuestion];
        Notify({ data: "success", msg: "Question added successfully", type: "success" });
      }
      return updatedGroups;
    });

    setEditingQuestionIdx(null);
    resetQuestion({
      question: "",
      options: ["", "", "", "", ""],
      answer: ""
    });

    isSubmittingRef.current = false;
    console.log('Submission completed for ID:', submissionId);
  };

  const editQuestion = (questionIdx) => {
    console.log('editQuestion called for index:', questionIdx);
    const question = testGroups[selectedTestGroupIdx].tests[questionIdx];
    console.log('Question data:', question);

    setEditingQuestionIdx(questionIdx);

    setQuestionValue("question", question.question, { shouldValidate: true, shouldDirty: true });
    question.options.forEach((opt, i) =>
      setQuestionValue(`options.${i}`, opt, { shouldValidate: true, shouldDirty: true })
    );
    setQuestionValue("answer", question.answer, { shouldValidate: false, shouldDirty: true });

    console.log('Form state after setValue:', {
      question: watchQuestion("question"),
      options: watchQuestion("options"),
      answer: watchQuestion("answer")
    });
  };

  const saveTests = () => {
    if (testGroups.length === 0) {
      Notify({ data: "error", msg: "No test groups created", type: "error" });
      return;
    }
    const updated = [...courseStructure];
    // Replace all test groups for the subject with the current testGroups
    updated[selectedLessonIdx].subjects[selectedSubjectIdx].content.test = [...testGroups];
    setCourseStructure(updated);
    setShowTestModal(false);
    setTestGroups([]);
    setSelectedTestGroupIdx(null);
    setEditingTestGroupIdx(null);
    setEditingQuestionIdx(null);
    Notify({ data: "success", msg: "Tests saved successfully", type: "success" });
  };

  const deleteTestGroup = (groupIdx) => {
    const updated = testGroups.filter((_, idx) => idx !== groupIdx);
    setTestGroups(updated);
    if (selectedTestGroupIdx === groupIdx) {
      setSelectedTestGroupIdx(updated.length > 0 ? 0 : null);
    }
    if (editingTestGroupIdx === groupIdx) {
      setEditingTestGroupIdx(null);
      resetTestGroup();
    }
  };

  const deleteQuestion = (questionIdx) => {
    const updatedGroups = [...testGroups];
    updatedGroups[selectedTestGroupIdx].tests = updatedGroups[selectedTestGroupIdx].tests.filter(
      (_, idx) => idx !== questionIdx
    );
    setTestGroups(updatedGroups);
    if (editingQuestionIdx === questionIdx) {
      setEditingQuestionIdx(null);
      resetQuestion({
        question: "",
        options: ["", "", "", "", ""],
        answer: ""
      });
    }
  };

  const onSubmit = (data) => {
    const formData = new FormData();
    formData.append("title", data.title || "");
    formData.append("description", data.description || "");
    formData.append("type", data.type || "Courses");
    formData.append("is_paid", isPaid);
    formData.append("price", isPaid ? (data.price || 0) : 0);
    if (thumbnail) {
      formData.append("thumbnail", thumbnail);
    }

    const structured = courseStructure.map((lesson, lIdx) => {
      if (lesson.thumbnail) {
        formData.append("files", lesson.thumbnail, lesson.thumbnail.name);
      }
      return {
        lesson_name: lesson.lesson_name || `Lesson ${lIdx + 1}`,
        description: lesson.description || "",
        thumbnail: lesson.thumbnail ? lesson.thumbnail.name : null,
        subjects: lesson.subjects.map((subject, sIdx) => {
          if (subject.thumbnail) {
            formData.append("files", subject.thumbnail, subject.thumbnail.name);
          }
          const content = {};
          ["pdf", "video", "image", "test"].forEach((type) => {
            content[type] = subject.content[type].map((item) => {
              if (type === "test") {
                return {
                  name: item.name,
                  tests: item.tests.map(test => ({
                    question: test.question,
                    options: test.options,
                    answer: test.answer
                  }))
                };
              }
              if (item.type === "youtube") {
                return { name: item.name, url: item.url, type: "youtube" };
              }
              formData.append("files", item.fileData, item.fileData.name);
              return { name: item.name, url: item.url, type: "file" };
            });
          });
          return {
            chapter_name: subject.chapter_name || `Subject ${sIdx + 1}`,
            description: subject.description || "",
            thumbnail: subject.thumbnail ? subject.thumbnail.name : null,
            content
          };
        })
      };
    });

    formData.append("lessons", JSON.stringify(structured));

    dispatch(createCourses(formData))
      .unwrap()
      .then((res) => {
        if (res?.success) {
          reset();
          Notify({ data: "success", msg: "Course created successfully", type: "success" });
          setCourseStructure([]);
          setThumbnail(null);
          setIsPaid(false);
          setCurrentStep(1);
          setSelectedLessonIdx(null);
          setSelectedSubjectIdx(null);
          setShowSidebar(false);
          setShowVideoPrompt(false);
          setIsYouTube(false);
          setYouTubeUrl("");
          setYouTubeError("");
          setActiveButtonId(null);
          setShowTestModal(false);
          setTestGroups([]);
          setSelectedTestGroupIdx(null);
          setEditingTestGroupIdx(null);
          setEditingQuestionIdx(null);
          dispatch(getAllCourses({ is_paid: "" }));
          dispatch(openCreateNewCourseDialog(false));
          navigate("/courses");
        }
      })
      .catch((err) => {
        Notify({ data: "error", msg: "Failed to create course", type: "error" });
      });
  };

  const handleAdd = (menuId, e) => {
    e.stopPropagation();
    setActiveButtonId(menuId);
    setTimeout(() => setActiveButtonId(null), 500);
    if (menuId.startsWith('lesson-')) {
      const lessonIdx = parseInt(menuId.split('-')[1], 10);
      addSubject(lessonIdx, true);
    }
  };

  const handleNavigateToContent = (lessonIdx, subjectIdx, e) => {
    e.stopPropagation();
    setSelectedLessonIdx(lessonIdx);
    setSelectedSubjectIdx(subjectIdx);
    setCurrentStep(4);
  };

  const stepLabels = ["Course Details", "Lesson", "Subject", "Content"];

  return (
    <Dialog
      open={showCreateNewCourseDialog}
      // onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      PaperProps={{ className: "create-course-dialog" }}
    >
      {loader ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "300px",
          }}
        >
          <Spinner />
        </div>
      ) : (
        <>
          <div className="dialog-content">
            {showSidebar && (
              <div className="sidebar">
                <div className="sidebar-header">
                  <h3>Course Structure</h3>
                  <button type="button" onClick={() => setShowSidebar(false)} className="close-sidebar-btn" title="Close sidebar">
                    <IoCloseCircleOutline
                      size={28}
                      style={{ cursor: "pointer", color: "#f44336" }}
                    />
                  </button>
                </div>
                <ul className="structure-list">
                  <li className="structure-item">
                    <div
                      className={`structure-title ${currentStep === 1 ? 'active' : ''}`}
                      onClick={() => {
                        setCurrentStep(1);
                        setSelectedLessonIdx(null);
                        setSelectedSubjectIdx(null);
                      }}
                    >
                      <span>Course Details</span>
                    </div>
                  </li>
                  {courseStructure.length === 0 ? (
                    <p className="no-data">No lessons added yet.</p>
                  ) : (
                    courseStructure.map((lesson, lIdx) => (
                      <li key={lIdx} className="structure-item">
                        <div
                          className={`structure-title ${selectedLessonIdx === lIdx && currentStep === 2 ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedLessonIdx(lIdx);
                            setSelectedSubjectIdx(null);
                            setCurrentStep(2);
                          }}
                        >
                          <span>{lesson.lesson_name || `Lesson ${lIdx + 1}`}</span>
                          <div className="structure-actions">
                            <button
                              type="button"
                              onClick={(e) => handleAdd(`lesson-${lIdx}`, e)}
                              className={`add-btn ${activeButtonId === `lesson-${lIdx}` ? 'active' : ''}`}
                              title="Add subject"
                              aria-label="Add subject"
                            >
                              <IoAdd />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteLesson(lIdx);
                              }}
                              className="delete-btn"
                              title="Delete lesson"
                              aria-label="Delete lesson"
                            >
                              <IoTrashOutline />
                            </button>
                          </div>
                        </div>
                        {lesson.subjects?.length > 0 && (
                          <ul className="sub-list">
                            {lesson.subjects.map((subject, sIdx) => (
                              <li key={sIdx}>
                                <div
                                  className={`structure-title ${selectedSubjectIdx === sIdx && selectedLessonIdx === lIdx && currentStep === 3 ? 'active' : ''}`}
                                  onClick={() => {
                                    setSelectedLessonIdx(lIdx);
                                    setSelectedSubjectIdx(sIdx);
                                    setCurrentStep(3);
                                  }}
                                >
                                  <span>{subject.chapter_name || `Subject ${sIdx + 1}`}</span>
                                  <div className="structure-actions">
                                    <button
                                      type="button"
                                      onClick={(e) => handleNavigateToContent(lIdx, sIdx, e)}
                                      className={`content-nav-btn ${selectedSubjectIdx === sIdx && selectedLessonIdx === lIdx && currentStep === 4 ? 'highlighted' : ''}`}
                                      title="Go to content"
                                      aria-label={`Go to content for ${subject.chapter_name || `Subject ${sIdx + 1}`}`}
                                    >
                                      <FontAwesomeIcon icon={faFolderOpen} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteSubject(lIdx, sIdx);
                                      }}
                                      className="delete-btn"
                                      title="Delete subject"
                                      aria-label="Delete subject"
                                    >
                                      <IoTrashOutline />
                                    </button>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))
                  )}
                </ul>
                <button type="button" onClick={addLesson} className="add-lesson-btn" title="Add new lesson" aria-label="Add new lesson">
                  Add Lesson
                </button>
              </div>
            )}
            <div className="main-content">
              <div className="header">
                <h2>Create New Course</h2>
                <div className="header-actions">
                  {!showSidebar && (
                    <button type="button" onClick={() => setShowSidebar(true)} className="show-structure-btn" title="Show course structure">
                      Show Course Structure
                    </button>
                  )}
                  <IoCloseCircleOutline
                    size={28}
                    onClick={handleClose}
                    style={{ cursor: "pointer", color: "#f44336" }}
                  />
                </div>
              </div>
              <div className="step-indicator">
                {stepLabels.map((label, idx) => (
                  <div key={idx} className={`step ${currentStep === idx + 1 ? 'active' : ''}`}>
                    <div className="step-number">{idx + 1}</div>
                    <p>{label}</p>
                  </div>
                ))}
              </div>
              <form onSubmit={handleSubmit(onSubmit)} className="form">
                {currentStep === 1 && (
                  <div className="form-section">
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        {...register("title", { required: "Title is required" })}
                        className="form-input"
                        aria-invalid={errors.title ? "true" : "false"}
                      />
                      {errors.title && <p className="error-text">{errors.title.message}</p>}
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        {...register("description", { required: "Description is required" })}
                        className="form-textarea"
                        aria-invalid={errors.description ? "true" : "false"}
                      />
                      {errors.description && <p className="error-text">{errors.description.message}</p>}
                    </div>
                    <div className="form-group">
                      <label>Thumbnail</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setThumbnail(e.target.files[0] || null)}
                        className="form-file"
                        aria-required="true"
                      />
                      {thumbnail && (
                        <img src={URL.createObjectURL(thumbnail)} alt="Course thumbnail preview" className="thumbnail-preview" />
                      )}
                    </div>
                    <div className="form-group checkbox">
                      <input
                        type="checkbox"
                        checked={isPaid}
                        onChange={() => setIsPaid(!isPaid)}
                        id="isPaid"
                      />
                      <label htmlFor="isPaid">Paid Course</label>
                    </div>
                    {isPaid && (
                      <div className="form-group">
                        <label>Price</label>
                        <input
                          type="number"
                          {...register("price", {
                            required: "Price is required for paid courses",
                            min: { value: 0, message: "Price cannot be negative" }
                          })}
                          className="form-input"
                          aria-invalid={errors.price ? "true" : "false"}
                        />
                        {errors.price && <p className="error-text">{errors.price.message}</p>}
                      </div>
                    )}
                  </div>
                )}
                {currentStep === 2 && selectedLessonIdx !== null && courseStructure[selectedLessonIdx] && (
                  <div className="form-section">
                    <h3>Lesson {selectedLessonIdx + 1}</h3>
                    <div className="form-group">
                      <label>Lesson Name</label>
                      <input
                        value={courseStructure[selectedLessonIdx].lesson_name || ""}
                        onChange={(e) => handleChange("lesson", selectedLessonIdx, null, "lesson_name", e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Lesson Description</label>
                      <textarea
                        value={courseStructure[selectedLessonIdx].description || ""}
                        onChange={(e) => handleChange("lesson", selectedLessonIdx, null, "description", e.target.value)}
                        className="form-textarea"
                      />
                    </div>
                    <div className="form-group">
                      <label>Lesson Thumbnail</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleChange("lesson", selectedLessonIdx, null, "thumbnail", e)}
                        className="form-file"
                      />
                      {courseStructure[selectedLessonIdx].thumbnail && (
                        <img
                          src={URL.createObjectURL(courseStructure[selectedLessonIdx].thumbnail)}
                          alt="Lesson thumbnail preview"
                          className="thumbnail-preview"
                        />
                      )}
                    </div>
                  </div>
                )}
                {currentStep === 3 && selectedLessonIdx !== null && selectedSubjectIdx !== null && (
                  <div className="form-section">
                    <h3>Subject {selectedSubjectIdx + 1}</h3>
                    <div className="form-group">
                      <label>Subject Name</label>
                      <input
                        value={courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].chapter_name || ""}
                        onChange={(e) => handleChange("subject", selectedLessonIdx, selectedSubjectIdx, "chapter_name", e.target.value)}
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Subject Description</label>
                      <textarea
                        value={courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].description || ""}
                        onChange={(e) => handleChange("subject", selectedLessonIdx, selectedSubjectIdx, "description", e.target.value)}
                        className="form-textarea"
                      />
                    </div>
                    <div className="form-group">
                      <label>Subject Thumbnail</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleChange("subject", selectedLessonIdx, selectedSubjectIdx, "thumbnail", e)}
                        className="form-file"
                      />
                      {courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].thumbnail && (
                        <img
                          src={URL.createObjectURL(courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].thumbnail)}
                          alt="Subject thumbnail preview"
                          className="thumbnail-preview"
                        />
                      )}
                    </div>
                  </div>
                )}
                {currentStep === 4 && selectedLessonIdx !== null && selectedSubjectIdx !== null && (
                  <div className="form-section content-section">
                    <h3>Add Content for Subject {selectedSubjectIdx + 1}</h3>
                    <div className="content-types">
                      {[
                        { type: 'pdf', label: 'PDF', icon: faFilePdf },
                        { type: 'video', label: 'Video', icon: faFilm },
                        { type: 'image', label: 'Image', icon: faImage },
                        { type: 'test', label: 'MCQ', icon: faPen }
                      ].map(({ type, label, icon }) => (
                        <div key={type} className="content-type-card" onClick={() => addContent(type)}>
                          <FontAwesomeIcon icon={icon} className="content-icon" />
                          <span>Add {label}</span>
                        </div>
                      ))}
                    </div>
                    {showVideoPrompt && (
                      <div className="video-input-options">
                        <label className="video-option-label">Select Video Source</label>
                        <div className="radio-group">
                          <label>
                            <input
                              type="radio"
                              checked={!isYouTube}
                              onChange={() => { setIsYouTube(false); setYouTubeUrl(""); setYouTubeError(""); }}
                            />
                            Upload Video File
                          </label>
                          <label>
                            <input
                              type="radio"
                              checked={isYouTube}
                              onChange={() => setIsYouTube(true)}
                            />
                            YouTube Link
                          </label>
                        </div>
                        {isYouTube ? (
                          <div className="youtube-input">
                            <input
                              type="text"
                              value={youTubeUrl}
                              onChange={(e) => {
                                setYouTubeUrl(e.target.value);
                                setYouTubeError("");
                              }}
                              placeholder="https://www.youtube.com/watch?v=..."
                              className="form-input"
                            />
                            {youTubeError && <p className="error-text">{youTubeError}</p>}
                            <div className="video-prompt-actions">
                              <button type="button" onClick={addVideoContent} className="action-btn youtube-btn">
                                Add YouTube Video
                              </button>
                              <button
                                type="button"
                                onClick={() => setShowVideoPrompt(false)}
                                className="cancel-btn"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="video-prompt-actions">
                            <button type="button" onClick={addVideoContent} className="action-btn">
                              Upload Video
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowVideoPrompt(false)}
                              className="cancel-btn"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="content-list">
                      {["pdf", "video", "image", "test"].map((type) => {
                        const files = courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].content[type];
                        return files.length > 0 ? (
                          <div key={type} className="content-group">
                            <h4>{type.charAt(0).toUpperCase() + type.slice(1)}</h4>
                            <div className="content-files">
                              {files.map((item, i) => (
                                <div key={i} className="content-file">
                                  {type === "video" && item.type === "youtube" ? (
                                    <a
                                      href={`https://www.youtube.com/watch?v=${getYouTubeVideoId(item.url)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        position: 'relative',
                                        display: 'inline-block',
                                        width: '100px',
                                        height: '60px',
                                        textDecoration: 'none'
                                      }}
                                    >
                                      <img
                                        src={`https://img.youtube.com/vi/${getYouTubeVideoId(item.url)}/0.jpg`}
                                        alt={item.name}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                      />
                                      <FontAwesomeIcon
                                        icon={faYoutube}
                                        style={{
                                          position: 'absolute',
                                          top: '50%',
                                          left: '50%',
                                          transform: 'translate(-50%, -50%)',
                                          color: 'red',
                                          fontSize: '30px',
                                          borderRadius: '50%',
                                          padding: '4px'
                                        }}
                                      />
                                    </a>
                                  ) : type === "image" ? (
                                    <img src={item.url} alt={item.name} className="content-preview" />
                                  ) : type === "test" ? (
                                    <div className="test-contentCreateCourse" style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      width: '100%',
                                      gap: '10px'
                                    }}>
                                      <span className="fs-6" style={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '70%',
                                        display: 'inline-block'
                                      }}>
                                        {item.name}
                                      </span>

                                      <div className="content-actions" style={{ display: 'flex', gap: '4px' }}>
                                        <button
                                          type="button"
                                          onClick={() => editTestGroupInContent(i)}
                                          className="edit-btn"
                                          title="Edit test group"
                                        >
                                          <FontAwesomeIcon icon={faEdit} style={{ fontSize: '15px' }} />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => deleteContent(type, i)}
                                          className="delete-btnTest"
                                          title="Delete test group"
                                        >
                                          <IoTrashOutline size={17} color="#dc3545" />
                                        </button>
                                      </div>
                                    </div>

                                  ) : (
                                    <FontAwesomeIcon
                                      icon={type === "pdf" ? faFilePdf : type === "video" ? faFilm : faImage}
                                      className="content-icon"
                                    />
                                  )}
                                  {type !== "test" && <span>{item.name}</span>}
                                  {type !== "test" && (
                                    <button
                                      type="button"
                                      onClick={() => deleteContent(type, i)}
                                      className="delete-file-btn"
                                    >
                                      <IoTrashOutline />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })}
                      {["pdf", "video", "image", "test"].every(
                        (type) => courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].content[type].length === 0
                      ) && <p className="no-data">No content added yet.</p>}
                    </div>
                  </div>
                )}
                <div className="form-actions">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (currentStep === 2) setCurrentStep(1);
                        else if (currentStep === 3) setCurrentStep(2);
                        else if (currentStep === 4) setCurrentStep(3);
                      }}
                      className="back-btn"
                    >
                      Back
                    </button>
                  )}
                  <div className="action-buttons">
                    {currentStep === 1 && (
                      <button type="button" onClick={addLesson} className="action-btn">
                        Next: Add Lesson
                      </button>
                    )}
                    {currentStep === 2 && (
                      <button type="button" onClick={() => addSubject(selectedLessonIdx, true)} className="action-btn">
                        Next: Add Subject
                      </button>
                    )}
                    {currentStep === 3 && (
                      <button type="button" onClick={() => setCurrentStep(4)} className="action-btn">
                        Next: Add Content
                      </button>
                    )}
                    <button type="submit" className="submit-btn">
                      Submit Course
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
          {showTestModal && (
            <Dialog
              open={showTestModal}
              // onClose={() => setShowTestModal(false)}
              maxWidth="md"
              PaperProps={{ className: "test-modal" }}
            >
              <div className="test-modal-content">
                <div className="test-modal-header">
                  <h2>Create/Edit Tests</h2>
                  <IoCloseCircleOutline
                    size={28}
                    onClick={() => setShowTestModal(false)}
                    style={{ cursor: "pointer", color: "#f44336" }}
                  />
                </div>
                <div className="test-modal-body">
                  <div className="test-group-form">
                    <h3>{editingTestGroupIdx !== null ? "Edit Test Name" : "Add Test Name"}</h3>
                    <form onSubmit={handleTestGroupSubmit(addTestGroup)}>
                      <div className="form-group">
                        <label>Test Name</label>
                        <input
                          {...registerTestGroup("testGroupName", { required: "Test group name is required" })}
                          className="form-input"
                          aria-invalid={testGroupErrors.testGroupName ? "true" : "false"}
                        />
                        {testGroupErrors.testGroupName && <p className="error-text">{testGroupErrors.testGroupName.message}</p>}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button type="submit" className="action-btn">
                          {editingTestGroupIdx !== null ? "Update Test Name" : "Add Test Name"}
                        </button>
                        {editingTestGroupIdx !== null && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingTestGroupIdx(null);
                              resetTestGroup();
                            }}
                            className="cancel-btn"
                          >
                            Cancel Edit
                          </button>
                        )}
                      </div>

                    </form>
                  </div>
                  {testGroups.length > 0 && (
                    <div className="test-group-list">
                      <h3>Test Names</h3>
                      <ul>
                        {testGroups.map((group, idx) => (
                          <li
                            key={idx}
                            className={`test-group-item ${selectedTestGroupIdx === idx ? 'active' : ''}`}
                            onClick={() => setSelectedTestGroupIdx(idx)}
                          >
                            <span>{group.name}</span>
                            <div className="test-group-actions">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  editTestGroup(idx);
                                }}
                                className="edit-btn"
                                title="Edit test group"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteTestGroup(idx);
                                }}
                                className="delete-btnTest"
                                title="Delete test group"
                              >
                                <IoTrashOutline />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedTestGroupIdx !== null && (
                    <div className="question-form">
                      <h3>
                        {editingQuestionIdx !== null
                          ? `Edit Question in ${testGroups[selectedTestGroupIdx].name}`
                          : `Add Questions to ${testGroups[selectedTestGroupIdx].name}`}
                      </h3>
                      <form onSubmit={handleQuestionSubmit(addQuestion)}>
                        <div className="form-group">
                          <label>Question</label>
                          <input
                            {...registerQuestion("question", { required: "Question is required" })}
                            className="form-input"
                            aria-invalid={questionErrors.question ? "true" : "false"}
                          />
                          {questionErrors.question && <p className="error-text">{questionErrors.question.message}</p>}
                        </div>
                        <div className="form-group">
                          <label>Options</label>
                          {Array(5)
                            .fill()
                            .map((_, i) => (
                              <div key={i} className="option-input">
                                <input
                                  {...registerQuestion(`options.${i}`, {
                                    required: `Option ${i + 1} is required`,
                                  })}
                                  className="form-input"
                                  placeholder={`Option ${i + 1}`}
                                  aria-invalid={questionErrors.options?.[i] ? "true" : "false"}
                                />
                                {questionErrors.options?.[i] && (
                                  <p className="error-text">{questionErrors.options[i].message}</p>
                                )}
                              </div>
                            ))}
                        </div>
                        <div className="form-group">
                          <label>Correct Answer</label>
                          <select
                            {...registerQuestion("answer", { required: "Correct answer is required" })}
                            className="form-input"
                            aria-invalid={questionErrors.answer ? "true" : "false"}
                            value={watchQuestion("answer") || ""}
                          >
                            <option value="">Select correct answer</option>
                            {watchQuestion("options")?.map((option, i) => (
                              <option key={i} value={option} disabled={!option}>
                                {option || `Option ${i + 1} (empty)`}
                              </option>
                            ))}
                          </select>
                          {questionErrors.answer && <p className="error-text">{questionErrors.answer.message}</p>}
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <button type="submit" className="action-btn">
                            {editingQuestionIdx !== null ? "Update Question" : "Add Question"}
                          </button>
                          {editingQuestionIdx !== null && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingQuestionIdx(null);
                                resetQuestion({
                                  question: "",
                                  options: ["", "", "", "", ""],
                                  answer: ""
                                });
                              }}
                              className="cancel-btn"
                            >
                              Cancel Edit
                            </button>
                          )}
                        </div>

                      </form>
                      {testGroups[selectedTestGroupIdx].tests.length > 0 && (
                        <div className="question-list">
                          <h4>Questions</h4>
                          <ul>
                            {testGroups[selectedTestGroupIdx].tests.map((test, qIdx) => (
                              <li key={qIdx} className="question-item">
                                <span>{test.question}</span>
                                <div className="question-actions">
                                  <button
                                    type="button"
                                    onClick={() => editQuestion(qIdx)}
                                    className="edit-btn"
                                    title="Edit question"
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteQuestion(qIdx)}
                                    className="delete-btnTest"
                                    title="Delete question"
                                  >
                                    <IoTrashOutline />
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="test-modal-actions">
                  <button type="button" onClick={saveTests} className="action-btn">Save Tests</button>
                  <button type="button" onClick={() => setShowTestModal(false)} className="cancel-btn">Cancel</button>
                </div>
              </div>
            </Dialog>
          )}
        </>
      )}
    </Dialog>
  );
};

export default CreateCourse;





// **************************************************************Without Test pop up************************************************************************
// import React, { useState } from "react";
// import { useForm } from "react-hook-form";
// import { useDispatch, useSelector } from "react-redux";
// import { createCourses, openCreateNewCourseDialog, getAllCourses } from "../Redux/Dashboard/DashboardSlice";
// import Dialog from "@mui/material/Dialog";
// import { useTheme } from "@mui/material/styles";
// import { useMediaQuery } from "@mui/material";
// import { IoCloseCircleOutline, IoTrashOutline, IoAdd } from "react-icons/io5";
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faFilePdf, faFilm, faImage, faPen, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
// import { Notify } from "../CommonPopup/DashboardPopUp";
// import Spinner from '../Spinner/Spinner';
// import '../Style/CreateCourse.css';
// import { useNavigate } from "react-router-dom";

// const CreateCourse = () => {
//   const theme = useTheme();
//   const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { showCreateNewCourseDialog, loader } = useSelector((s) => s.DashboardSlice);

//   const [courseStructure, setCourseStructure] = useState([]);
//   const [thumbnail, setThumbnail] = useState(null);
//   const [isPaid, setIsPaid] = useState(false);
//   const [currentStep, setCurrentStep] = useState(1);
//   const [selectedLessonIdx, setSelectedLessonIdx] = useState(null);
//   const [selectedSubjectIdx, setSelectedSubjectIdx] = useState(null);
//   const [showSidebar, setShowSidebar] = useState(false);
//   const [showVideoPrompt, setShowVideoPrompt] = useState(false);
//   const [isYouTube, setIsYouTube] = useState(false);
//   const [youTubeUrl, setYouTubeUrl] = useState("");
//   const [youTubeError, setYouTubeError] = useState("");
//   const [activeButtonId, setActiveButtonId] = useState(null);

//   const { register, handleSubmit, reset, formState: { errors }, getValues } = useForm({
//     defaultValues: { type: "Courses" }
//   });

//   const getYouTubeVideoId = (url) => {
//     try {
//       const regex = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/;
//       const match = url.match(regex);
//       return match && match[1] ? match[1] : null;
//     } catch (error) {
//       console.error('Error parsing YouTube URL:', error);
//       return null;
//     }
//   };

//   const fetchYouTubeVideoTitle = async (url) => {
//     try {
//       const response = await fetch(`https://noembed.com/embed?format=json&url=${encodeURIComponent(url)}`);
//       const data = await response.json();
//       if (data.error) {
//         throw new Error(data.error);
//       }
//       return data.title || null;
//     } catch (error) {
//       console.error('Error fetching YouTube video title:', error);
//       return null;
//     }
//   };

//   const addVideoContent = async () => {
//     if (isYouTube) {
//       if (!youTubeUrl) {
//         setYouTubeError("Please enter a YouTube URL.");
//         return;
//       }
//       const videoId = getYouTubeVideoId(youTubeUrl);
//       if (!videoId) {
//         setYouTubeError("Invalid YouTube URL.");
//         return;
//       }
//       const videoTitle = await fetchYouTubeVideoTitle(youTubeUrl);
//       if (!videoTitle) {
//         setYouTubeError("Failed to fetch video title. Please try again.");
//         return;
//       }
//       const updated = [...courseStructure];
//       updated[selectedLessonIdx].subjects[selectedSubjectIdx].content.video.push({
//         name: videoTitle,
//         url: youTubeUrl,
//         type: "youtube"
//       });
//       setCourseStructure(updated);
//       setYouTubeUrl("");
//       setYouTubeError("");
//       setShowVideoPrompt(false);
//       Notify({ data: "success", msg: "YouTube video added successfully", type: "success" });
//     } else {
//       const input = document.createElement("input");
//       input.type = "file";
//       input.accept = "video/mp4";
//       input.multiple = true;
//       input.onchange = (e) => {
//         const files = Array.from(e.target.files).map((file) => ({
//           name: file.name,
//           url: URL.createObjectURL(file),
//           fileData: file,
//           type: "file"
//         }));
//         const updated = [...courseStructure];
//         updated[selectedLessonIdx].subjects[selectedSubjectIdx].content.video = [
//           ...updated[selectedLessonIdx].subjects[selectedSubjectIdx].content.video,
//           ...files
//         ];
//         setCourseStructure(updated);
//         setShowVideoPrompt(false);
//         Notify({ data: "success", msg: "Video file(s) added successfully", type: "success" });
//       };
//       input.click();
//     }
//   };

//   const handleClose = () => {
//     reset();
//     setCourseStructure([]);
//     setThumbnail(null);
//     setIsPaid(false);
//     setCurrentStep(1);
//     setSelectedLessonIdx(null);
//     setSelectedSubjectIdx(null);
//     setShowSidebar(false);
//     setShowVideoPrompt(false);
//     setIsYouTube(false);
//     setYouTubeUrl("");
//     setYouTubeError("");
//     setActiveButtonId(null);
//     dispatch(openCreateNewCourseDialog(false));
//   };

//   const addLesson = () => {
//     const values = getValues();
//     const hasErrors = Object.keys(errors).length > 0;
//     const isTitleValid = values.title && values.title.trim() !== "";
//     const isDescriptionValid = values.description && values.description.trim() !== "";
//     const isThumbnailValid = thumbnail !== null;

//     if (!isTitleValid || !isDescriptionValid || !isThumbnailValid || hasErrors) {
//       Notify({
//         data: "error",
//         msg: "Please fill all course details.",
//         type: "error"
//       });
//       return;
//     }

//     const newLesson = { lesson_name: "", description: "", thumbnail: null, subjects: [] };
//     setCourseStructure([...courseStructure, newLesson]);
//     setSelectedLessonIdx(courseStructure.length);
//     setSelectedSubjectIdx(null);
//     setCurrentStep(2);
//     setShowSidebar(true);
//     Notify({ data: "success", msg: "Lesson added successfully", type: "success" });
//   };

//   const deleteLesson = (lessonIdx) => {
//     const updated = courseStructure.filter((_, idx) => idx !== lessonIdx);
//     setCourseStructure(updated);
//     if (selectedLessonIdx === lessonIdx) {
//       if (updated.length > 0) {
//         const newIdx = lessonIdx < updated.length ? lessonIdx : updated.length - 1;
//         setSelectedLessonIdx(newIdx);
//         setSelectedSubjectIdx(null);
//         setCurrentStep(2);
//       } else {
//         setSelectedLessonIdx(null);
//         setSelectedSubjectIdx(null);
//         setCurrentStep(1);
//       }
//     }
//   };

//   const addSubject = (lessonIdx, navigate = false) => {
//     if (lessonIdx === null) return;
//     const updated = [...courseStructure];
//     updated[lessonIdx].subjects.push({
//       chapter_name: "",
//       description: "",
//       thumbnail: null,
//       content: { pdf: [], video: [], image: [], test: [] }
//     });
//     setCourseStructure(updated);
//     if (navigate) {
//       setSelectedLessonIdx(lessonIdx);
//       setSelectedSubjectIdx(updated[lessonIdx].subjects.length - 1);
//       setCurrentStep(3);
//     }
//   };

//   const deleteSubject = (lessonIdx, subjectIdx) => {
//     const updated = [...courseStructure];
//     updated[lessonIdx].subjects = updated[lessonIdx].subjects.filter((_, idx) => idx !== subjectIdx);
//     setCourseStructure(updated);
//     if (selectedLessonIdx === lessonIdx && selectedSubjectIdx === subjectIdx) {
//       setSelectedSubjectIdx(null);
//       setCurrentStep(2);
//     }
//   };

//   const deleteContent = (type, fileIdx) => {
//     if (selectedLessonIdx === null || selectedSubjectIdx === null) return;
//     const updated = [...courseStructure];
//     updated[selectedLessonIdx].subjects[selectedSubjectIdx].content[type] =
//       updated[selectedLessonIdx].subjects[selectedSubjectIdx].content[type].filter((_, idx) => idx !== fileIdx);
//     setCourseStructure(updated);
//   };

//   const handleChange = (level, lIdx, sIdx, field, value) => {
//     const updated = [...courseStructure];
//     let target = updated[lIdx];
//     if (sIdx !== null) target = target.subjects[sIdx];
//     if (field === "thumbnail") {
//       target[field] = value.target.files[0] || null;
//     } else {
//       target[field] = value;
//     }
//     setCourseStructure(updated);
//   };

//   const addContent = (type) => {
//     if (selectedLessonIdx === null || selectedSubjectIdx === null) return;
//     if (type === "video") {
//       setShowVideoPrompt(true);
//       setIsYouTube(false);
//       setYouTubeUrl("");
//       setYouTubeError("");
//       return;
//     }
//     const accept = type === "pdf" ? ".pdf" : type === "image" ? "image/*" : "*";
//     const input = document.createElement("input");
//     input.type = "file";
//     input.accept = accept;
//     input.multiple = true;
//     input.onchange = (e) => {
//       const files = Array.from(e.target.files).map((file) => ({
//         name: file.name,
//         url: URL.createObjectURL(file),
//         fileData: file,
//         type: "file"
//       }));
//       const updated = [...courseStructure];
//       updated[selectedLessonIdx].subjects[selectedSubjectIdx].content[type] = [
//         ...updated[selectedLessonIdx].subjects[selectedSubjectIdx].content[type],
//         ...files
//       ];
//       setCourseStructure(updated);
//       Notify({ data: "success", msg: `${type.charAt(0).toUpperCase() + type.slice(1)} file(s) added successfully`, type: "success" });
//     };
//     input.click();
//   };

//   const onSubmit = (data) => {
//     const formData = new FormData();
//     formData.append("title", data.title || "");
//     formData.append("description", data.description || "");
//     formData.append("type", data.type || "Courses");
//     formData.append("is_paid", isPaid);
//     formData.append("price", isPaid ? (data.price || 0) : 0);
//     if (thumbnail) {
//       formData.append("thumbnail", thumbnail);
//     }

//     const structured = courseStructure.map((lesson, lIdx) => {
//       if (lesson.thumbnail) {
//         formData.append("files", lesson.thumbnail, lesson.thumbnail.name);
//       }
//       return {
//         lesson_name: lesson.lesson_name || `Lesson ${lIdx + 1}`,
//         description: lesson.description || "",
//         thumbnail: lesson.thumbnail ? lesson.thumbnail.name : null,
//         subjects: lesson.subjects.map((subject, sIdx) => {
//           if (subject.thumbnail) {
//             formData.append("files", subject.thumbnail, subject.thumbnail.name);
//           }
//           const content = {};
//           ["pdf", "video", "image", "test"].forEach((type) => {
//             content[type] = subject.content[type].map((file) => {
//               if (file.type === "youtube") {
//                 return { name: file.name, url: file.url, type: "youtube" };
//               }
//               formData.append("files", file.fileData, file.fileData.name);
//               return { name: file.name, url: file.url, type: "file" };
//             });
//           });
//           return {
//             chapter_name: subject.chapter_name || `Subject ${sIdx + 1}`,
//             description: subject.description || "",
//             thumbnail: subject.thumbnail ? subject.thumbnail.name : null,
//             content
//           };
//         })
//       };
//     });

//     formData.append("lessons", JSON.stringify(structured));

//     dispatch(createCourses(formData))
//       .unwrap()
//       .then((res) => {
//         if (res?.success) {
//           reset();
//           Notify({ data: "success", msg: res.message || "Course created successfully", type: "success" });
//           setCourseStructure([]);
//           setThumbnail(null);
//           setIsPaid(false);
//           setCurrentStep(1);
//           setSelectedLessonIdx(null);
//           setSelectedSubjectIdx(null);
//           setShowSidebar(false);
//           setShowVideoPrompt(false);
//           setIsYouTube(false);
//           setYouTubeUrl("");
//           setYouTubeError("");
//           setActiveButtonId(null);
//           dispatch(getAllCourses({ is_paid: "" }));
//           dispatch(openCreateNewCourseDialog(false));
//           navigate("/courses");
//         }
//       })
//       .catch((err) => {
//         Notify({ data: "error", msg: err.message || "Failed to create course", type: "error" });
//       });
//   };

//   const handleAdd = (menuId, e) => {
//     e.stopPropagation();
//     setActiveButtonId(menuId);
//     setTimeout(() => setActiveButtonId(null), 500);
//     if (menuId.startsWith('lesson-')) {
//       const lessonIdx = parseInt(menuId.split('-')[1], 10);
//       addSubject(lessonIdx, true);
//     }
//   };

//   const handleNavigateToContent = (lessonIdx, subjectIdx, e) => {
//     e.stopPropagation();
//     setSelectedLessonIdx(lessonIdx);
//     setSelectedSubjectIdx(subjectIdx);
//     setCurrentStep(4);
//   };

//   const stepLabels = ["Course Details", "Lesson", "Subject", "Content"];

//   return (
//     <Dialog
//       open={showCreateNewCourseDialog}
//       onClose={handleClose}
//       fullScreen={fullScreen}
//       maxWidth="lg"
//       PaperProps={{ className: "create-course-dialog" }}
//     >
//       {loader ? (
//         <div
//           style={{
//             display: "flex",
//             justifyContent: "center",
//             alignItems: "center",
//             height: "100%", // Ensures full height of dialog
//             minHeight: "300px", // Fallback height for content-less dialog
//           }}
//         >
//           <Spinner />
//         </div>
//       ) :
//         <>
//           <div className="dialog-content">
//             {showSidebar && (
//               <div className="sidebar">
//                 <div className="sidebar-header">
//                   <h3>Course Structure</h3>
//                   <button onClick={() => setShowSidebar(false)} className="close-sidebar-btn" title="Close sidebar">
//                     <IoCloseCircleOutline
//                       size={28}
//                       onClick={() => setShowSidebar(false)}
//                       style={{ cursor: "pointer", color: "#f44336" }}
//                     />
//                   </button>
//                 </div>
//                 <ul className="structure-list">
//                   <li className="structure-item">
//                     <div
//                       className={`structure-title ${currentStep === 1 ? 'active' : ''}`}
//                       onClick={() => {
//                         setCurrentStep(1);
//                         setSelectedLessonIdx(null);
//                         setSelectedSubjectIdx(null);
//                       }}
//                     >
//                       <span>Course Details</span>
//                     </div>
//                   </li>
//                   {courseStructure.length === 0 ? (
//                     <p className="no-data">No lessons added yet.</p>
//                   ) : (
//                     courseStructure.map((lesson, lIdx) => (
//                       <li key={lIdx} className="structure-item">
//                         <div
//                           className={`structure-title ${selectedLessonIdx === lIdx && currentStep === 2 ? 'active' : ''}`}
//                           onClick={() => {
//                             setSelectedLessonIdx(lIdx);
//                             setSelectedSubjectIdx(null);
//                             setCurrentStep(2);
//                           }}
//                         >
//                           <span>{lesson.lesson_name || `Lesson ${lIdx + 1}`}</span>
//                           <div className="structure-actions">
//                             <button
//                               onClick={(e) => handleAdd(`lesson-${lIdx}`, e)}
//                               className={`add-btn ${activeButtonId === `lesson-${lIdx}` ? 'active' : ''}`}
//                               title="Add subject"
//                               aria-label="Add subject"
//                             >
//                               <IoAdd />
//                             </button>
//                             <button
//                               onClick={(e) => {
//                                 e.stopPropagation();
//                                 deleteLesson(lIdx);
//                               }}
//                               className="delete-btn"
//                               title="Delete lesson"
//                               aria-label="Delete lesson"
//                             >
//                               <IoTrashOutline />
//                             </button>
//                           </div>
//                         </div>
//                         {lesson.subjects?.length > 0 && (
//                           <ul className="sub-list">
//                             {lesson.subjects.map((subject, sIdx) => (
//                               <li key={sIdx}>
//                                 <div
//                                   className={`structure-title ${selectedSubjectIdx === sIdx && selectedLessonIdx === lIdx && currentStep === 3 ? 'active' : ''}`}
//                                   onClick={() => {
//                                     setSelectedLessonIdx(lIdx);
//                                     setSelectedSubjectIdx(sIdx);
//                                     setCurrentStep(3);
//                                   }}
//                                 >
//                                   <span>{subject.chapter_name || `Subject ${sIdx + 1}`}</span>
//                                   <div className="structure-actions">
//                                     <button
//                                       onClick={(e) => handleNavigateToContent(lIdx, sIdx, e)}
//                                       className={`content-nav-btn ${selectedSubjectIdx === sIdx && selectedLessonIdx === lIdx && currentStep === 4 ? 'highlighted' : ''}`}
//                                       title="Go to content"
//                                       aria-label={`Go to content for ${subject.chapter_name || `Subject ${sIdx + 1}`}`}
//                                     >
//                                       <FontAwesomeIcon icon={faFolderOpen} />
//                                     </button>
//                                     <button
//                                       onClick={(e) => {
//                                         e.stopPropagation();
//                                         deleteSubject(lIdx, sIdx);
//                                       }}
//                                       className="delete-btn"
//                                       title="Delete subject"
//                                       aria-label="Delete subject"
//                                     >
//                                       <IoTrashOutline />
//                                     </button>
//                                   </div>
//                                 </div>
//                               </li>
//                             ))}
//                           </ul>
//                         )}
//                       </li>
//                     ))
//                   )}
//                 </ul>
//                 <button onClick={addLesson} className="add-lesson-btn" title="Add new lesson" aria-label="Add new lesson">
//                   Add Lesson
//                 </button>
//               </div>
//             )}
//             <div className="main-content">
//               <div className="header">
//                 <h2>Create New Course</h2>
//                 <div className="header-actions">
//                   {!showSidebar && (
//                     <button onClick={() => setShowSidebar(true)} className="show-structure-btn" title="Show course structure">
//                       Show Course Structure
//                     </button>
//                   )}
//                   <IoCloseCircleOutline
//                     size={28}
//                     onClick={handleClose}
//                     style={{ cursor: "pointer", color: "#f44336" }}
//                   />
//                 </div>
//               </div>
//               <div className="step-indicator">
//                 {stepLabels.map((label, idx) => (
//                   <div key={idx} className={`step ${currentStep === idx + 1 ? 'active' : ''}`}>
//                     <div className="step-number">{idx + 1}</div>
//                     <p>{label}</p>
//                   </div>
//                 ))}
//               </div>
//               <form onSubmit={handleSubmit(onSubmit)} className="form">
//                 {currentStep === 1 && (
//                   <div className="form-section">
//                     <div className="form-group">
//                       <label>Title</label>
//                       <input
//                         {...register("title", { required: "Title is required" })}
//                         className="form-input"
//                         aria-invalid={errors.title ? "true" : "false"}
//                       />
//                       {errors.title && <p className="error-text">{errors.title.message}</p>}
//                     </div>
//                     <div className="form-group">
//                       <label>Description</label>
//                       <textarea
//                         {...register("description", { required: "Description is required" })}
//                         className="form-textarea"
//                         aria-invalid={errors.description ? "true" : "false"}
//                       />
//                       {errors.description && <p className="error-text">{errors.description.message}</p>}
//                     </div>
//                     <div className="form-group">
//                       <label>Thumbnail</label>
//                       <input
//                         type="file"
//                         accept="image/*"
//                         onChange={(e) => setThumbnail(e.target.files[0] || null)}
//                         className="form-file"
//                         aria-required="true"
//                       />
//                       {thumbnail && (
//                         <img src={URL.createObjectURL(thumbnail)} alt="Course thumbnail preview" className="thumbnail-preview" />
//                       )}
//                     </div>
//                     <div className="form-group checkbox">
//                       <input
//                         type="checkbox"
//                         checked={isPaid}
//                         onChange={() => setIsPaid(!isPaid)}
//                         id="isPaid"
//                       />
//                       <label htmlFor="isPaid">Paid Course</label>
//                     </div>
//                     {isPaid && (
//                       <div className="form-group">
//                         <label>Price</label>
//                         <input
//                           type="number"
//                           {...register("price", {
//                             required: "Price is required for paid courses",
//                             min: { value: 0, message: "Price cannot be negative" }
//                           })}
//                           className="form-input"
//                           aria-invalid={errors.price ? "true" : "false"}
//                         />
//                         {errors.price && <p className="error-text">{errors.price.message}</p>}
//                       </div>
//                     )}
//                   </div>
//                 )}
//                 {currentStep === 2 && selectedLessonIdx !== null && courseStructure[selectedLessonIdx] && (
//                   <div className="form-section">
//                     <h3>Lesson {selectedLessonIdx + 1}</h3>
//                     <div className="form-group">
//                       <label>Lesson Name</label>
//                       <input
//                         value={courseStructure[selectedLessonIdx].lesson_name || ""}
//                         onChange={(e) => handleChange("lesson", selectedLessonIdx, null, "lesson_name", e.target.value)}
//                         className="form-input"
//                       />
//                     </div>
//                     <div className="form-group">
//                       <label>Lesson Description</label>
//                       <textarea
//                         value={courseStructure[selectedLessonIdx].description || ""}
//                         onChange={(e) => handleChange("lesson", selectedLessonIdx, null, "description", e.target.value)}
//                         className="form-textarea"
//                       />
//                     </div>
//                     <div className="form-group">
//                       <label>Lesson Thumbnail</label>
//                       <input
//                         type="file"
//                         accept="image/*"
//                         onChange={(e) => handleChange("lesson", selectedLessonIdx, null, "thumbnail", e)}
//                         className="form-file"
//                       />
//                       {courseStructure[selectedLessonIdx].thumbnail && (
//                         <img
//                           src={URL.createObjectURL(courseStructure[selectedLessonIdx].thumbnail)}
//                           alt="Lesson thumbnail preview"
//                           className="thumbnail-preview"
//                         />
//                       )}
//                     </div>
//                   </div>
//                 )}
//                 {currentStep === 3 && selectedLessonIdx !== null && selectedSubjectIdx !== null && (
//                   <div className="form-section">
//                     <h3>Subject {selectedSubjectIdx + 1}</h3>
//                     <div className="form-group">
//                       <label>Subject Name</label>
//                       <input
//                         value={courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].chapter_name || ""}
//                         onChange={(e) => handleChange("subject", selectedLessonIdx, selectedSubjectIdx, "chapter_name", e.target.value)}
//                         className="form-input"
//                       />
//                     </div>
//                     <div className="form-group">
//                       <label>Subject Description</label>
//                       <textarea
//                         value={courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].description || ""}
//                         onChange={(e) => handleChange("subject", selectedLessonIdx, selectedSubjectIdx, "description", e.target.value)}
//                         className="form-textarea"
//                       />
//                     </div>
//                     <div className="form-group">
//                       <label>Subject Thumbnail</label>
//                       <input
//                         type="file"
//                         accept="image/*"
//                         onChange={(e) => handleChange("subject", selectedLessonIdx, selectedSubjectIdx, "thumbnail", e)}
//                         className="form-file"
//                       />
//                       {courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].thumbnail && (
//                         <img
//                           src={URL.createObjectURL(courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].thumbnail)}
//                           alt="Subject thumbnail preview"
//                           className="thumbnail-preview"
//                         />
//                       )}
//                     </div>
//                   </div>
//                 )}
//                 {currentStep === 4 && selectedLessonIdx !== null && selectedSubjectIdx !== null && (
//                   <div className="form-section content-section">
//                     <h3>Add Content for Subject {selectedSubjectIdx + 1}</h3>
//                     <div className="content-types">
//                       {[
//                         { type: 'pdf', label: 'PDF', icon: faFilePdf },
//                         { type: 'video', label: 'Video', icon: faFilm },
//                         { type: 'image', label: 'Image', icon: faImage },
//                         { type: 'test', label: 'Test', icon: faPen }
//                       ].map(({ type, label, icon }) => (
//                         <div key={type} className="content-type-card" onClick={() => addContent(type)}>
//                           <FontAwesomeIcon icon={icon} className="content-icon" />
//                           <span>Add {label}</span>
//                         </div>
//                       ))}
//                     </div>
//                     {showVideoPrompt && (
//                       <div className="video-input-options">
//                         <label className="video-option-label">Select Video Source</label>
//                         <div className="radio-group">
//                           <label>
//                             <input
//                               type="radio"
//                               checked={!isYouTube}
//                               onChange={() => { setIsYouTube(false); setYouTubeUrl(""); setYouTubeError(""); }}
//                             />
//                             Upload Video File
//                           </label>
//                           <label>
//                             <input
//                               type="radio"
//                               checked={isYouTube}
//                               onChange={() => setIsYouTube(true)}
//                             />
//                             YouTube Link
//                           </label>
//                         </div>
//                         {isYouTube ? (
//                           <div className="youtube-input">
//                             <input
//                               type="text"
//                               value={youTubeUrl}
//                               onChange={(e) => {
//                                 setYouTubeUrl(e.target.value);
//                                 setYouTubeError("");
//                               }}
//                               placeholder="https://www.youtube.com/watch?v=..."
//                               className="form-input"
//                             />
//                             {youTubeError && <p className="error-text">{youTubeError}</p>}
//                             <div className="video-prompt-actions">
//                               <button type="button" onClick={addVideoContent} className="action-btn youtube-btn">
//                                 Add YouTube Video
//                               </button>
//                               <button
//                                 type="button"
//                                 onClick={() => setShowVideoPrompt(false)}
//                                 className="cancel-btnCreate"
//                               >
//                                 Cancel
//                               </button>
//                             </div>
//                           </div>
//                         ) : (
//                           <div className="video-prompt-actions">
//                             <button type="button" onClick={addVideoContent} className="action-btn">
//                               Upload Video
//                             </button>
//                             <button
//                               type="button"
//                               onClick={() => setShowVideoPrompt(false)}
//                               className="cancel-btn"
//                             >
//                               Cancel
//                             </button>
//                           </div>
//                         )}
//                       </div>
//                     )}
//                     <div className="content-list">
//                       {["pdf", "video", "image", "test"].map((type) => {
//                         const files = courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].content[type];
//                         return files.length > 0 ? (
//                           <div key={type} className="content-group">
//                             <h4>{type.charAt(0).toUpperCase() + type.slice(1)}</h4>
//                             <div className="content-files">
//                               {files.map((file, i) => (
//                                 <div key={i} className="content-file">
//                                   {type === "video" && file.type === "youtube" ? (
//                                     <iframe
//                                       width="100"
//                                       height="60"
//                                       src={`https://www.youtube.com/embed/${getYouTubeVideoId(file.url)}`}
//                                       title={file.name}
//                                       className="content-preview"
//                                     />
//                                   ) : type === "image" ? (
//                                     <img src={file.url} alt={file.name} className="content-preview" />
//                                   ) : (
//                                     <FontAwesomeIcon
//                                       icon={type === "pdf" ? faFilePdf : type === "video" ? faFilm : type === "image" ? faImage : faPen}
//                                       className="content-icon"
//                                     />
//                                   )}
//                                   <span>{file.name}</span>
//                                   <button onClick={() => deleteContent(type, i)} className="delete-file-btn">
//                                     <IoTrashOutline />
//                                   </button>
//                                 </div>
//                               ))}
//                             </div>
//                           </div>
//                         ) : null;
//                       })}
//                       {["pdf", "video", "image", "test"].every(
//                         (type) => courseStructure[selectedLessonIdx].subjects[selectedSubjectIdx].content[type].length === 0
//                       ) && <p className="no-data">No content added yet.</p>}
//                     </div>
//                   </div>
//                 )}
//                 <div className="form-actions">
//                   {currentStep > 1 && (
//                     <button
//                       type="button"
//                       onClick={() => {
//                         if (currentStep === 2) setCurrentStep(1);
//                         else if (currentStep === 3) setCurrentStep(2);
//                         else if (currentStep === 4) setCurrentStep(3);
//                       }}
//                       className="back-btn"
//                     >
//                       Back
//                     </button>
//                   )}
//                   <div className="action-buttons">
//                     {currentStep === 1 && (
//                       <button type="button" onClick={addLesson} className="action-btn">
//                         Next: Add Lesson
//                       </button>
//                     )}
//                     {currentStep === 2 && (
//                       <button type="button" onClick={() => addSubject(selectedLessonIdx, true)} className="action-btn">
//                         Next: Add Subject
//                       </button>
//                     )}
//                     {currentStep === 3 && (
//                       <button type="button" onClick={() => setCurrentStep(4)} className="action-btn">
//                         Next: Add Content
//                       </button>
//                     )}

//                     <button type="submit" className="submit-btn">
//                       Submit Course
//                     </button>

//                   </div>
//                 </div>
//               </form>
//             </div>
//           </div>
//         </>
//       }

//     </Dialog>
//   );
// };

// export default CreateCourse;
